// src/components/Navbar.js
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaSignOutAlt,
  FaClock,
  FaChevronDown,
} from "react-icons/fa";
import { getActiveToken, getActiveUserRaw, setActiveUserJson } from "../utils/authStorage";

const AUTH_API = process.env.REACT_APP_AUTH_SERVICE_URL || process.env.REACT_APP_DEVICE_SERVICE_URL || "";
const STORE_API = process.env.REACT_APP_STORE_SERVICE_URL || "";

const getToken = () => getActiveToken() || null;
const authHeaders = () => {
  const token = getToken();
  const base = { "Content-Type": "application/json" };
  return token ? { ...base, Authorization: `Bearer ${token}` } : base;
};
const parseJsonSafe = async (res) => { try { return await res.json(); } catch { return null; } };

// Pastilles rôle (tons doux + léger relief)
const ROLE_STYLES = {
  admin: { bg: "bg-rose-100", text: "text-rose-800", ring: "ring-rose-200/80" },
  manager: { bg: "bg-sky-100", text: "text-sky-900", ring: "ring-sky-200/80" },
  marketing: { bg: "bg-violet-100", text: "text-violet-900", ring: "ring-violet-200/80" },
  finance: { bg: "bg-amber-100", text: "text-amber-950", ring: "ring-amber-200/80" },
  merchant: { bg: "bg-teal-100", text: "text-teal-900", ring: "ring-teal-200/80" },
  user: { bg: "bg-emerald-100", text: "text-emerald-900", ring: "ring-emerald-200/80" },
};

export default function Navbar() {
  const navigate = useNavigate();
  const [now, setNow] = useState(new Date());

  // profile & store
  const [user, setUser] = useState(() => {
    try { 
      const raw = getActiveUserRaw(); 
      return raw ? JSON.parse(raw) : null; 
    } catch { 
      return null; 
    }
  });
  
  const [store, setStore] = useState(() => {
    try {
      if (user?.store && typeof user.store === "object") return user.store;
      if (user?.storeId && typeof user.storeId === "object") return user.storeId;
      return null;
    } catch { 
      return null; 
    }
  });
  
  const [profileLoading, setProfileLoading] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const userRef = useRef(null);

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (userRef.current && !userRef.current.contains(e.target)) setUserOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const handleLogout = () => {
    try {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      sessionStorage.removeItem("token");
      sessionStorage.removeItem("user");
    } catch {}
    try {
      if (window.axios && window.axios.defaults) delete window.axios.defaults.headers.common?.Authorization;
      if (typeof require === "function") {
        const ax = require("axios");
        if (ax?.defaults) delete ax.defaults.headers.common?.Authorization;
      }
    } catch {}
    navigate("/login");
  };

  // helper: compute a friendly store label, with fallbacks for manager users
  const getStoreLabel = (userObj, storeObj) => {
    // prefer resolved store object
    if (storeObj && (storeObj.name || storeObj.code)) return `${storeObj.name || ""}${storeObj.code ? ` (${storeObj.code})` : ""}`;

    // check user embedded store object
    const embeddedStore = userObj?.store && typeof userObj.store === 'object' ? userObj.store : (userObj?.storeId && typeof userObj.storeId === 'object' ? userObj.storeId : null);
    if (embeddedStore && (embeddedStore.name || embeddedStore.code)) return `${embeddedStore.name || ""}${embeddedStore.code ? ` (${embeddedStore.code})` : ""}`;

    // some backends send storeName / storeCode directly on user
    if (userObj?.storeName || userObj?.storeCode) return `${userObj.storeName || ""}${userObj.storeCode ? ` (${userObj.storeCode})` : ""}`;

    // if manager but we only have an id, show a label with the id (better than generic 'Manager')
    if (userObj?.role === 'manager') {
      if (typeof userObj.storeId === 'string' && userObj.storeId.length > 0) return `Magasin — ${userObj.storeId}`;
      return 'Manager';
    }

    if (userObj?.role === 'merchant') {
      const merchant = userObj.merchantId && typeof userObj.merchantId === 'object' ? userObj.merchantId : null;
      if (merchant?.name) return merchant.name;
      if (typeof userObj.merchantId === 'string') return `Tous les magasins`;
      return 'Mes magasins';
    }

    if ((userObj?.role === 'admin')||(userObj?.role === 'finance')||(userObj?.role === 'marketing')) return 'Tous les magasins';
    return 'Système de gestion';
  };

  // fetch profile logic (unchanged except we store resolved store)
  useEffect(() => {
    let mounted = true;

    const fetchStoreById = async (storeId) => {
      if (!storeId) return null;
      const candidates = [STORE_API, AUTH_API].filter(Boolean);
      for (const base of candidates) {
        try {
          const res = await fetch(`${base.replace(/\/$/, "")}/stores/${storeId}`, { 
            headers: authHeaders(), 
            mode: "cors" 
          });
          if (res.status === 401) { handleLogout(); return null; }
          if (!res.ok) continue;
          const d = await parseJsonSafe(res);
          const s = d && d.store ? d.store : d;
          if (s) return s;
        } catch (e) { /* ignore */ }
      }
      return null;
    };

    const fetchProfile = async () => {
      const token = getToken();
      if (!token) return;
      try {
        setProfileLoading(true);
        const res = await fetch(`${AUTH_API.replace(/\/$/, "")}/profile`, { 
          headers: authHeaders(), 
          mode: "cors" 
        });
        if (res.status === 401) { handleLogout(); return; }
        if (!res.ok) return;
        const data = await parseJsonSafe(res);
        const u = data && data.user ? data.user : (data && data.success && data.user ? data.user : data);
        if (!u) return;

        let resolvedStore = u.store && typeof u.store === "object" ? u.store
                           : (u.storeId && typeof u.storeId === "object" ? u.storeId : null);

        const maybeStoreId = !resolvedStore && (typeof u.storeId === "string" ? u.storeId : (typeof u.store === "string" ? u.store : null));
        if (!resolvedStore && maybeStoreId) {
          const s = await fetchStoreById(maybeStoreId);
          if (s) resolvedStore = s;
        }

        if (mounted) {
          const merged = resolvedStore ? ({ ...u, store: resolvedStore, storeId: resolvedStore }) : u;
          setUser(merged);
          if (resolvedStore) setStore(resolvedStore);
          try { setActiveUserJson(merged); } catch {}
        }
      } catch (err) {
        // ignore
      } finally {
        if (mounted) setProfileLoading(false);
      }
    };

    fetchProfile();
    return () => { mounted = false; };
  }, []);

  // Presentation helpers
  const fullname = user ? `${user.name || ""}${user.lastName ? " " + user.lastName : ""}`.trim() : "";
  const displayName = fullname || user?.email || "Utilisateur";
  const role = (user?.role || "user").toLowerCase();
  const roleStyle = ROLE_STYLES[role] || ROLE_STYLES["user"];
  const roleBadgeClass = `inline-flex items-center rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide ring-1 ring-inset ${roleStyle.bg} ${roleStyle.text} ${roleStyle.ring}`;
  const initials = (() => {
    if (!user) return "U";
    const name = `${user.name || ""} ${user.lastName || ""}`.trim();
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "U";
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
  })();

  // NEW: compute store label with helper so managers get name + code when possible
  const storeLabel = getStoreLabel(user, store);

  return (
    <header className="sticky top-0 z-50 border-b border-slate-200 bg-white">
      <div className="flex h-11 items-center justify-between gap-4 px-4 sm:h-12 sm:px-6">
        {/* Contexte magasin */}
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-slate-800" title={storeLabel}>
            {storeLabel}
          </p>
          <p className="truncate text-[11px] text-slate-400">Système de gestion ECRLink</p>
        </div>

        {/* Horloge */}
        <div className="hidden shrink-0 items-center gap-2 text-slate-500 md:flex">
          <FaClock className="h-3.5 w-3.5 text-slate-400" />
          <span className="font-mono text-sm font-medium tabular-nums text-slate-700">
            {now.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
          </span>
          <span className="text-slate-300">·</span>
          <span className="text-xs capitalize">
            {now.toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" })}
          </span>
        </div>

        {/* Utilisateur */}
        <div ref={userRef} className="relative shrink-0">
          <button
            type="button"
            onClick={() => setUserOpen((v) => !v)}
            aria-expanded={userOpen}
            aria-haspopup="true"
            className="flex items-center gap-2 rounded-lg px-2 py-1 transition hover:bg-slate-50"
          >
            <div className="relative">
              <div className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-slate-700 text-xs font-semibold text-white">
                {user?.avatarUrl ? (
                  <img src={user.avatarUrl} alt="" className="h-full w-full object-cover" />
                ) : (
                  <span>{initials}</span>
                )}
              </div>
              <span className="absolute bottom-0 right-0 h-2 w-2 rounded-full border border-white bg-emerald-500" title="Connecté" />
            </div>
            <div className="hidden min-w-0 text-left sm:block">
              <p className="max-w-[9rem] truncate text-xs font-medium text-slate-800 lg:max-w-[12rem]">
                {displayName}
              </p>
              <p className="text-[10px] capitalize text-slate-400">{role}</p>
            </div>
            <FaChevronDown
              className={`h-3 w-3 text-slate-400 transition-transform ${userOpen ? "rotate-180" : ""}`}
            />
          </button>

          {userOpen && (
            <div className="absolute right-0 z-50 mt-1 w-60 rounded-lg border border-slate-200 bg-white py-1 shadow-lg sm:w-64">
              <div className="border-b border-slate-100 px-4 py-3">
                <p className="truncate text-sm font-medium text-slate-900">{displayName}</p>
                <p className="truncate text-xs text-slate-500">{user?.email}</p>
                <span className={`mt-2 ${roleBadgeClass}`}>{role}</span>
              </div>

              <div className="border-b border-slate-100 px-4 py-2.5 sm:hidden">
                <p className="text-[10px] uppercase tracking-wide text-slate-400">Magasin</p>
                <p className="truncate text-sm text-slate-700">{storeLabel}</p>
              </div>

              <button
                type="button"
                onClick={handleLogout}
                className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-rose-600 transition hover:bg-rose-50"
              >
                <FaSignOutAlt className="h-3.5 w-3.5" />
                Se déconnecter
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
